import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Card,
  DatePicker,
  Spin,
  message,
  Button,
  Empty,
  Tooltip as AntTooltip,
  Tag,
  Collapse
} from "antd";
import {
  ReloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  SwapOutlined,
  RiseOutlined,
  FallOutlined
} from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import moment from "moment";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import {
  buildAccountPairGroups,
  buildPairSplitGroups,
  buildExchangeVenueVolumeGroups,
} from "../../utils/tradeTrendChartData";
import { formatExchangeName, formatChartTooltipValue } from "../../utils/formatters";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import HedgePanoramaSection from "../HedgeCharts/HedgePanoramaSection";
import { Shield } from "lucide-react";
import { withOwnerUserId } from "../../utils/ownerScopeParams";
import { useAdminPanoramaUsers } from "../../hooks/useAdminPanoramaUsers";
import PanoramaExportButtons from "../common/PanoramaExportButtons";
import {
  resolvePanoramaExportOwnerUserId,
  exportTradeDataPanoramaExcel,
} from "../../utils/panoramaExportExcel";
import { capturePanoramaToPdf } from "../../utils/panoramaPdfCapture";
import "./TradeDataChart.css";

// 注册 Chart.js 组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const { RangePicker } = DatePicker;


// 颜色配置 - 专业金融风格
const COLORS = {
  primary: "#1890ff",
  success: "#52c41a",
  warning: "#faad14",
  error: "#ff4d4f",
  purple: "#722ed1",
  cyan: "#13c2c2",
  // 多系列颜色
  series: [
    { primary: "#1890ff", secondary: "rgba(24, 144, 255, 0.15)" },
    { primary: "#52c41a", secondary: "rgba(82, 196, 26, 0.15)" },
    { primary: "#faad14", secondary: "rgba(250, 173, 20, 0.15)" },
    { primary: "#f5222d", secondary: "rgba(245, 34, 45, 0.15)" },
    { primary: "#722ed1", secondary: "rgba(114, 46, 209, 0.15)" },
    { primary: "#13c2c2", secondary: "rgba(19, 194, 194, 0.15)" },
    { primary: "#eb2f96", secondary: "rgba(235, 47, 150, 0.15)" },
    { primary: "#fa8c16", secondary: "rgba(250, 140, 22, 0.15)" },
    { primary: "#2f54eb", secondary: "rgba(47, 84, 235, 0.15)" },
    { primary: "#a0d911", secondary: "rgba(160, 217, 17, 0.15)" },
  ],
};

// 快速日期选项（近 N 天、本月均不含「今天」，截止昨天）
const QUICK_DATES = [
  { label: "今天", value: () => [moment(), moment()] },
  {
    label: "近1天",
    value: () => {
      const y = moment().subtract(1, "days");
      return [y.clone().startOf("day"), y.clone().endOf("day")];
    },
  },
  {
    label: "近2天",
    value: () => [moment().subtract(2, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
  },
  {
    label: "近3天",
    value: () => [moment().subtract(3, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
  },
  {
    label: "近7天",
    value: () => [moment().subtract(7, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
  },
  {
    label: "近30天",
    value: () => [moment().subtract(30, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
  },
  {
    label: "本月",
    value: () => {
      const end = moment().subtract(1, "days").endOf("day");
      const start = moment().startOf("month").startOf("day");
      if (start.isAfter(end)) {
        return [end.clone().startOf("day"), end];
      }
      return [start, end];
    },
  },
  { label: "上月", value: () => [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")] },
];

function TradeDataChartBody({
  ownerUserId = null,
  dateRange,
  setDateRange,
  activeQuickDate,
  setActiveQuickDate,
  containerRef,
}) {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 数据状态
  const [summary, setSummary] = useState(null);
  const [totalTrendData, setTotalTrendData] = useState({ datasets: [] });

  /** 账号+交易所维度，内含现货/合约分图与各交易对曲线 */
  const [accountPairGroups, setAccountPairGroups] = useState([]);
  /** 交易对维度，内含现货/合约分图 */
  const [pairSplitGroups, setPairSplitGroups] = useState([]);
  /** 交易所维度日趋势，内含现货/合约分图 */
  const [exchangeVenueGroups, setExchangeVenueGroups] = useState([]);
  const [canViewHedge] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return hasPermission(u, PERMISSIONS.VIEW_HEDGE_GROUPS);
    } catch {
      return false;
    }
  });

  // 格式化函数
  const formatVolume = useCallback((val) => {
    if (!val && val !== 0) return "0";
    if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
    if (val >= 1000) return (val / 1000).toFixed(1) + "K";
    return val.toFixed(2);
  }, []);

  // 格式化显示值（用于标签）
  const formatLabelValue = useCallback((val) => {
    if (!val && val !== 0) return "";
    if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
    if (val >= 1000) return (val / 1000).toFixed(1) + "K";
    return val.toFixed(0);
  }, []);

  // 获取数据
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = withOwnerUserId(
        {
          date_from: dateRange[0].format("YYYY-MM-DD"),
          date_to: dateRange[1].format("YYYY-MM-DD"),
        },
        ownerUserId,
      );

      const [summaryRes, dailyRes, accountPairRes, pairTrendRes, exchangeDailyRes] = await Promise.all([
        apiService.trades.getTradeStats(params),
        apiService.trades.getDailyStats({ ...params, limit: 100 }),
        apiService.trades.getAccountPairTrendData(params),
        apiService.trades.getPairTrendData(params),
        apiService.trades.getExchangeDailyTrendData(params),
      ]);

      // 1. 处理总结数据
      setSummary(summaryRes.data.data);

      // 2. 处理总趋势数据
      const dailyRaw = dailyRes.data.data || [];
      const rDailyRaw = [...dailyRaw].reverse();
      const labels = rDailyRaw.map(item => moment(item.date).format("MM-DD"));
      const totalVolumes = rDailyRaw.map(item => parseFloat(item.total_volume || 0));
      const makerVolumes = rDailyRaw.map(item => parseFloat(item.maker_volume || 0));

      setTotalTrendData({
        labels,
        datasets: [
          {
            label: "总交易量",
            data: totalVolumes,
            borderColor: COLORS.primary,
            backgroundColor: "rgba(24, 144, 255, 0.1)",
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            yAxisID: 'y',
          },
          {
            label: "Maker量",
            data: makerVolumes,
            borderColor: COLORS.success,
            backgroundColor: "rgba(82, 196, 26, 0.1)",
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            yAxisID: 'y1',
          }
        ]
      });

      const accountPairRaw = accountPairRes.data.data || [];
      setAccountPairGroups(buildAccountPairGroups(accountPairRaw));

      const pairRaw = pairTrendRes.data.data || [];
      setPairSplitGroups(buildPairSplitGroups(pairRaw));

      const exchangeDailyRaw = exchangeDailyRes.data.data || [];
      setExchangeVenueGroups(buildExchangeVenueVolumeGroups(exchangeDailyRaw));

    } catch (error) {
      console.error(error);
      message.error("加载数据失败: " + (error.apiMessage || error.message));
    } finally {
      setLoading(false);
    }
  }, [dateRange, ownerUserId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // 统计卡片数据
  const statsData = useMemo(() => {
    if (!summary) return [];
    return [
      {
        key: "total",
        title: "总交易量",
        value: summary.total_volume || 0,
        icon: <DollarOutlined />,
        color: COLORS.primary,
        classSuffix: "total"
      },
      {
        key: "records",
        title: "总记录数",
        value: summary.total_records || 0,
        icon: <FileTextOutlined />,
        color: COLORS.success,
        classSuffix: "volume"
      },
      {
        key: "accounts",
        title: "活跃账户",
        value: accountPairGroups.length || 0,
        icon: <UserOutlined />,
        color: COLORS.purple,
        classSuffix: "accounts"
      },
      {
        key: "pairs",
        title: "活跃交易对",
        value: pairSplitGroups.length || 0,
        icon: <SwapOutlined />,
        color: COLORS.warning,
        classSuffix: "count"
      }
    ];
  }, [summary, accountPairGroups.length, pairSplitGroups.length]);

  // 单线条图表配置（带数值标签）- 双Y轴
  const singleLineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: true, position: 'top', align: 'end' },
      datalabels: {
        display: true,
        color: "#ffffff",
        backgroundColor: (context) => {
          return context.dataset.yAxisID === 'y1' ? "rgba(82, 196, 26, 0.8)" : "rgba(24, 144, 255, 0.8)";
        },
        borderRadius: 4,
        padding: { top: 2, bottom: 2, left: 6, right: 6 },
        font: {
          size: 11,
          weight: "bold"
        },
        anchor: "end",
        align: "top",
        offset: 4,
        formatter: (value) => formatLabelValue(value)
      },
      tooltip: {
        backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
        titleColor: isDark ? "#ffffff" : "#1f2937",
        bodyColor: isDark ? "rgba(255, 255, 255, 0.85)" : "#374151",
        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatChartTooltipValue(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { color: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)" },
        ticks: { color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.45)" }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: { color: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)" },
        ticks: {
          color: COLORS.primary,
          callback: (value) => formatVolume(value)
        },
        title: {
          display: true,
          text: '总交易量',
          color: COLORS.primary
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false, // 只显示轴线，不显示网格线
        },
        ticks: {
          color: COLORS.success,
          callback: (value) => formatVolume(value)
        },
        title: {
          display: true,
          text: 'Maker量',
          color: COLORS.success
        }
      }
    }
  }), [formatVolume, formatLabelValue, isDark, formatChartTooltipValue]);

  // 生成独立卡片图表配置 - 双Y轴
  const getCardChartOptions = useCallback((color) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: false, // 小卡片图表不显示标签，避免拥挤
      },
      tooltip: {
        backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
        titleColor: isDark ? "#ffffff" : "#1f2937",
        bodyColor: isDark ? "rgba(255, 255, 255, 0.85)" : "#374151",
        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
        padding: 8,
        cornerRadius: 6,
        displayColors: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatChartTooltipValue(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
          font: { size: 10 },
          maxRotation: 45
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: { color: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)" },
        ticks: {
          color: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
          font: { size: 10 },
          callback: (value) => formatVolume(value)
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: COLORS.success,
          font: { size: 10 },
          callback: (value) => formatVolume(value)
        }
      }
    }
  }), [formatVolume, isDark, formatChartTooltipValue]);

  /** 多交易对 + 合计线：单 Y 轴、显示图例 */
  const multiLinePairBreakdownOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: true, position: "top", align: "start", labels: { boxWidth: 10, font: { size: 10 } } },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
        titleColor: isDark ? "#ffffff" : "#1f2937",
        bodyColor: isDark ? "rgba(255, 255, 255, 0.85)" : "#374151",
        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
        padding: 8,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatChartTooltipValue(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
          font: { size: 10 },
          maxRotation: 45,
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        beginAtZero: true,
        grid: { color: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)" },
        ticks: {
          color: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
          font: { size: 10 },
          callback: (value) => formatVolume(value),
        },
      },
    },
  }), [formatVolume, isDark, formatChartTooltipValue]);

  // 全屏切换
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`trade-data-chart ${isFullscreen ? "fullscreen" : ""}`}>
      {ownerUserId != null && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="blue">用户数据 · ID {ownerUserId}</Tag>
        </div>
      )}
      <div className="trade-chart-inner-toolbar" style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <Button
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchDashboardData}
          loading={loading}
        >
          刷新本面板
        </Button>
        <Button
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? "退出全屏" : "全屏"}
        </Button>
      </div>

      <Spin spinning={loading} tip="正在加载数据...">
        {/* 统计卡片区 */}
        <div className="stats-grid">
          {statsData.map(stat => (
            <Card key={stat.key} className={`stat-card stat-card-${stat.classSuffix}`}>
              <div className="stat-header">
                <div className={`stat-icon ${stat.classSuffix}`}>
                  {stat.icon}
                </div>
                <div className="stat-label">{stat.title}</div>
              </div>
              <div className="stat-value" style={{ color: stat.color }}>
                {stat.key === "total" ? formatVolume(stat.value) : stat.value.toLocaleString()}
                <span className="stat-unit">
                  {stat.key === "total" ? " USDT" : (stat.key === "records" ? " 条" : (stat.key === "accounts" || stat.key === "pairs" ? " 个" : ""))}
                </span>
              </div>
            </Card>
          ))}
        </div>

        {/* 图表区域 - 使用折叠面板 */}
        <Collapse
          defaultActiveKey={
            canViewHedge
              ? ["total", "hedge", "exchanges", "accounts", "pairs"]
              : ["total", "exchanges", "accounts", "pairs"]
          }
          className="chart-collapse"
          style={{
            background: "transparent",
            border: "none"
          }}
          items={[
            {
              key: "total",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", fontWeight: 600 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: COLORS.primary
                  }} />
                  总交易量趋势
                  {summary && (
                    <Tag color={summary.trend >= 0 ? "success" : "error"} style={{ marginLeft: 8 }}>
                      {summary.trend >= 0 ? <RiseOutlined /> : <FallOutlined />}
                      {Math.abs(summary.trend || 0).toFixed(1)}%
                    </Tag>
                  )}
                </span>
              ),
              children: (
                <div className="chart-wrapper trend-chart-wrapper" style={{ height: 320 }}>
                  {totalTrendData.labels?.length > 0 ? (
                    <Line data={totalTrendData} options={singleLineOptions} />
                  ) : (
                    <Empty description="暂无历史数据" style={{ marginTop: 80 }} />
                  )}
                </div>
              ),
              style: {
                background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: 12,
                marginBottom: 16,
                border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                overflow: "hidden"
              }
            },
            ...(canViewHedge
              ? [
                  {
                    key: "hedge",
                    label: (
                      <span style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", fontWeight: 600 }}>
                        <Shield size={16} style={{ opacity: 0.9 }} />
                        对冲组合分析（资金视角）
                        <AntTooltip title="与上方成交量趋势对照：各组每日总交易量走势与区间合计（trade_volumes）、组合资金/净资产、区间盈亏与对冲效率；资金来自权益快照。">
                          <InfoCircleOutlined style={{ fontSize: 14, opacity: 0.55 }} />
                        </AntTooltip>
                      </span>
                    ),
                    children: (
                      <div style={{ padding: "4px 8px 12px" }}>
                        <HedgePanoramaSection
                          dateFrom={dateRange[0].format("YYYY-MM-DD")}
                          dateTo={dateRange[1].format("YYYY-MM-DD")}
                          ownerUserId={ownerUserId}
                        />
                      </div>
                    ),
                    style: {
                      background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                      borderRadius: 12,
                      marginBottom: 16,
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                      overflow: "hidden",
                    },
                  },
                ]
              : []),
            {
              key: "exchanges",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", fontWeight: 600 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: COLORS.cyan,
                    }}
                  />
                  各交易所交易量趋势 ({exchangeVenueGroups.length} 个交易所)
                </span>
              ),
              children: exchangeVenueGroups.length > 0 ? (
                <div className="exchange-venue-stack">
                  {exchangeVenueGroups.map((eg) => (
                    <React.Fragment key={eg.key}>
                      {eg.spot.hasData && (
                        <div className="exchange-venue-chart-row">
                          <div className="exchange-venue-row-head">
                            <div className="exchange-venue-row-head-main">
                              <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                              <span className="exchange-venue-row-kind">现货（全市场汇总）</span>
                            </div>
                            <span className="exchange-venue-row-total">
                              周期 T: {formatVolume(eg.periodVolume)}
                            </span>
                          </div>
                          <div className="exchange-venue-chart-canvas">
                            <Line
                              data={eg.spot.chartData}
                              options={getCardChartOptions(COLORS.primary)}
                            />
                          </div>
                        </div>
                      )}
                      {eg.swap.hasData && (
                        <div className="exchange-venue-chart-row">
                          <div className="exchange-venue-row-head">
                            <div className="exchange-venue-row-head-main">
                              <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                              <span className="exchange-venue-row-kind">合约（全市场汇总）</span>
                            </div>
                            {!eg.spot.hasData && (
                              <span className="exchange-venue-row-total">
                                周期 T: {formatVolume(eg.periodVolume)}
                              </span>
                            )}
                          </div>
                          <div className="exchange-venue-chart-canvas">
                            <Line
                              data={eg.swap.chartData}
                              options={getCardChartOptions(COLORS.purple)}
                            />
                          </div>
                        </div>
                      )}
                      {!eg.spot.hasData && !eg.swap.hasData && (
                        <div className="exchange-venue-chart-row exchange-venue-chart-row--empty">
                          <div className="exchange-venue-row-head">
                            <span className="exchange-venue-row-title">{formatExchangeName(eg.exchange)}</span>
                          </div>
                          <Empty description="该交易所无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <Empty description="暂无交易所趋势数据" style={{ marginTop: 80 }} />
              ),
              style: {
                background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: 12,
                marginBottom: 16,
                border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                overflow: "hidden",
              },
            },
            {
              key: "accounts",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", fontWeight: 600 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: COLORS.success
                  }} />
                  各账号交易量趋势 ({accountPairGroups.length} 个账号)
                </span>
              ),
              children: accountPairGroups.length > 0 ? (
                <div className="chart-grid">
                  {accountPairGroups.map((g) => (
                    <div key={g.key} className="chart-card account-pair-group-card">
                      <div className="chart-card-header">
                        <span className="chart-card-title">
                          {g.account} <span className="chart-card-exchange">({g.exchange})</span>
                        </span>
                        <span className="chart-card-total">
                          周期合计 T: {formatVolume(g.periodVolume)}
                        </span>
                      </div>
                      <div className="chart-card-body account-pair-split-body">
                        {g.spot.hasData && (
                          <div className="trade-type-subchart">
                            <div className="subchart-title">现货 · 按交易对 + 合计</div>
                            <div className="chart-inner" style={{ height: 220 }}>
                              <Line
                                data={g.spot.chartData}
                                options={multiLinePairBreakdownOptions}
                              />
                            </div>
                          </div>
                        )}
                        {g.swap.hasData && (
                          <div className="trade-type-subchart">
                            <div className="subchart-title">合约 · 按交易对 + 合计</div>
                            <div className="chart-inner" style={{ height: 220 }}>
                              <Line
                                data={g.swap.chartData}
                                options={multiLinePairBreakdownOptions}
                              />
                            </div>
                          </div>
                        )}
                        {!g.spot.hasData && !g.swap.hasData && (
                          <Empty description="该账号无分交易对数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="暂无账号数据" style={{ marginTop: 80 }} />
              ),
              style: {
                background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: 12,
                marginBottom: 16,
                border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                overflow: "hidden"
              }
            },
            {
              key: "pairs",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "inherit", fontWeight: 600 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: COLORS.warning
                  }} />
                  各交易对交易量趋势 ({pairSplitGroups.length} 个交易对)
                </span>
              ),
              children: pairSplitGroups.length > 0 ? (
                <div className="chart-grid">
                  {pairSplitGroups.map((pg) => (
                    <div key={pg.key} className="chart-card pair-split-card">
                      <div className="chart-card-header">
                        <span className="chart-card-title">{pg.pair}</span>
                        {pg.exchanges ? (
                          <span className="chart-card-exchange">{pg.exchanges}</span>
                        ) : null}
                        <span className="chart-card-total">
                          周期 T: {formatVolume(pg.periodVolume)}
                        </span>
                      </div>
                      <div className="chart-card-body pair-split-body">
                        {pg.spot.hasData && (
                          <div className="trade-type-subchart">
                            <div className="subchart-title">现货</div>
                            <div className="chart-inner" style={{ height: 200 }}>
                              <Line
                                data={pg.spot.chartData}
                                options={getCardChartOptions(COLORS.primary)}
                              />
                            </div>
                          </div>
                        )}
                        {pg.swap.hasData && (
                          <div className="trade-type-subchart">
                            <div className="subchart-title">合约</div>
                            <div className="chart-inner" style={{ height: 200 }}>
                              <Line
                                data={pg.swap.chartData}
                                options={getCardChartOptions(COLORS.purple)}
                              />
                            </div>
                          </div>
                        )}
                        {!pg.spot.hasData && !pg.swap.hasData && (
                          <Empty description="无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="暂无交易对数据" style={{ marginTop: 80 }} />
              ),
              style: {
                background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: 12,
                border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                overflow: "hidden"
              }
            }
          ]}
        />
      </Spin>
    </div>
  );
}

function TradeDataChart() {
  const containerRef = useRef(null);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days").startOf("day"),
    moment().subtract(1, "days").endOf("day"),
  ]);
  const [activeQuickDate, setActiveQuickDate] = useState("近7天");
  const [adminPanoramaKey, setAdminPanoramaKey] = useState(["all"]);
  const { isDark } = useTheme();
  const { isAdmin, adminUsers, loadingUsers } = useAdminPanoramaUsers();

  const exportOwnerUserId = useMemo(
    () => resolvePanoramaExportOwnerUserId(isAdmin, adminPanoramaKey),
    [isAdmin, adminPanoramaKey],
  );

  const dateFromStr = dateRange[0]?.format("YYYY-MM-DD");
  const dateToStr = dateRange[1]?.format("YYYY-MM-DD");

  const handleExportExcel = useCallback(async () => {
    if (!dateFromStr || !dateToStr) return;
    const hide = message.loading("正在导出 Excel...", 0);
    try {
      await exportTradeDataPanoramaExcel({
        dateFrom: dateFromStr,
        dateTo: dateToStr,
        ownerUserId: exportOwnerUserId,
      });
      message.success("Excel 已下载");
    } catch (e) {
      console.error(e);
      message.error(e.apiMessage || e.message || "导出失败");
    } finally {
      hide();
    }
  }, [dateFromStr, dateToStr, exportOwnerUserId]);

  const handleExportPdf = useCallback(async () => {
    if (!dateFromStr || !dateToStr) return;
    const hide = message.loading("正在展开图表并生成 PDF...", 0);
    try {
      await capturePanoramaToPdf(containerRef.current, `交易数据全景分析_${dateFromStr}_${dateToStr}`);
      message.success("PDF 已下载");
    } catch (e) {
      console.error(e);
      message.error(e.message || "PDF 导出失败");
    } finally {
      hide();
    }
  }, [dateFromStr, dateToStr]);

  const handleQuickDate = (label) => {
    const option = QUICK_DATES.find((d) => d.label === label);
    if (option) {
      setDateRange(option.value());
      setActiveQuickDate(label);
    }
  };

  const bodyProps = {
    dateRange,
    setDateRange,
    activeQuickDate,
    setActiveQuickDate,
    containerRef,
  };

  const pageHeader = (
    <div className="dashboard-header">
      <h1 className="dashboard-title">
        交易数据全景分析
        <AntTooltip title="支持总趋势、分交易所（现货/合约分图）、分账号按交易对、分交易对等多维视图。">
          <InfoCircleOutlined style={{ fontSize: 16, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)", marginLeft: 8 }} />
        </AntTooltip>
        {isAdmin && (
          <Tag color="processing" style={{ marginLeft: 12, fontWeight: 500 }}>
            管理员可展开查看各用户名下明细
          </Tag>
        )}
      </h1>
      <div className="trade-chart-header-toolbar">
        <div className="quick-date-buttons" role="group" aria-label="快捷日期">
          {QUICK_DATES.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`quick-date-btn ${activeQuickDate === item.label ? "active" : ""}`}
              onClick={() => handleQuickDate(item.label)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates);
            setActiveQuickDate("");
          }}
          allowClear={false}
          className="trade-chart-range-picker"
        />
        <PanoramaExportButtons onExportExcel={handleExportExcel} onExportPdf={handleExportPdf} />
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="trade-data-chart" ref={containerRef}>
        {pageHeader}
        <TradeDataChartBody ownerUserId={null} {...bodyProps} />
      </div>
    );
  }

  return (
    <div className="trade-data-chart" ref={containerRef}>
      {pageHeader}
      <Spin spinning={loadingUsers}>
        <Collapse
          destroyOnHidden
          activeKey={adminPanoramaKey}
          onChange={(keys) => setAdminPanoramaKey(Array.isArray(keys) ? keys : [keys])}
          className="admin-user-panorama-collapse"
          style={{ background: "transparent", border: "none" }}
          items={[
            {
              key: "all",
              label: "全平台汇总（全部用户数据）",
              children: <TradeDataChartBody ownerUserId={null} {...bodyProps} />,
            },
            ...adminUsers
              .filter((u) => u.role === "user")
              .map((u) => ({
                key: String(u.id),
                label: `${u.username}（ID ${u.id}）${u.email ? ` · ${u.email}` : ""}`,
                children: <TradeDataChartBody ownerUserId={u.id} {...bodyProps} />,
              })),
          ]}
        />
      </Spin>
    </div>
  );
}

export default TradeDataChart;
